import { faEnvelope } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import MUIFormControl from '@mui/material/FormControl';
import type { SelectChangeEvent } from '@mui/material/Select/SelectInput';
import type { CreateOrderRequest, ISO4217, SvtCreateOrderResponse } from '@partful/shared-types';
import { UseMutateAsyncFunction } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { ChangeEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TelephoneInput } from 'src/components/TextInput/TelephoneInput';
import { countryList } from 'src/constants/countryList';
import useCheckoutConfig from 'src/hooks/useCheckoutConfig';
import useDynamoTranslation from 'src/hooks/useTranslation';
import { CartEntry } from 'src/providers/ShoppingCart';
import { AccountDetails, MutateAccountDetails } from 'src/providers/User/userDetails';
import ConfigurableCheckOutInputField from '../ConfigurableCheckOutInputField';
import {
  CartButton,
  CheckoutActionBar,
  CheckOutCheckBox,
  CheckoutFormWrapper,
  CheckOutInputField,
  CheckOutSelectField,
  CheckoutSelectLabel,
  CheckOutSelectOption,
  CheckOutSubTitle,
  CheckOutTitle,
  FormContainer,
  LeftSection,
  LongCheckOutInputField,
  OptionalCheckoutLabel,
  OrderDetailsDivider,
  RightSection,
  Row,
  SectionsContainer,
  StyledMUICheckbox,
} from '../styles';
import { RawFormState, serialiseOrderData } from '../types';

interface CheckoutFormProps {
  cart: CartEntry[];
  discountPercentage?: number;
  currency: ISO4217;
  sendOrder: UseMutateAsyncFunction<AxiosResponse<SvtCreateOrderResponse>, unknown, CreateOrderRequest, unknown>;
  isOrderPOR: boolean;
  isFetching: boolean;
  defaultAccountDetails: MutateAccountDetails & AccountDetails;
  ctaKey?: string;
}

export function DesktopCheckoutForm({
  cart,
  discountPercentage,
  currency,
  sendOrder,
  isOrderPOR,
  isFetching,
  defaultAccountDetails,
  ctaKey,
}: CheckoutFormProps) {
  const { name, companyName, telephone, buildingName, streetName, city, postalCode, country, applyUserDetails } =
    defaultAccountDetails;

  const defaultButtonText = isOrderPOR ? /* istanbul ignore next */ 'details.requestQuote' : 'details.orderParts';

  const [formState, setFormState] = useState<RawFormState>({
    firstName: name,
    lastName: name,
    companyName,
    telephone,
    buildingName,
    streetName,
    city,
    postalCode,
    country,
    saveDetails: true,
    'vtaas-ui:checkout:customerAccountField': undefined,
    'vtaas-ui:checkout:customerReferenceField': undefined,
    'vtaas-ui:checkout:messageToSellerField': undefined,
  });

  const [validate, setValidate] = useState(false);

  const { t } = useTranslation(['orders', 'common']);

  const checkoutFormConfig = useCheckoutConfig();

  const handleChange = async (e: ChangeEvent<any> | SelectChangeEvent<unknown>) => {
    const { name } = e.target;
    if (e.target.type === 'checkbox') {
      setFormState((formState) => ({ ...formState, [name]: e.target.checked }));
    } else {
      setFormState((formState) => ({ ...formState, [name]: e.target.value }));
    }
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    /* istanbul ignore next */
    if (!formState.telephone) {
      return false;
    }

    const newOrder = serialiseOrderData(formState, cart, currency, discountPercentage);
    await sendOrder(newOrder); // need to await this because the apply user details will clear the cart before the order has been processed

    if (formState.saveDetails) {
      const { firstName, companyName, telephone, buildingName, streetName, city, postalCode, country } = formState;
      applyUserDetails({
        name: firstName,
        companyName,
        telephone,
        buildingName: buildingName || '',
        streetName,
        city,
        postalCode,
        country,
        currency,
      });
    }
  };

  const validateOnSubmission = (condition: boolean) => validate && condition;

  return (
    <CheckoutFormWrapper onSubmit={handleSubmit} data-testid="checkout-form">
      <FormContainer>
        <CheckOutTitle>{t('orders:details.yourOrderDetails')}</CheckOutTitle>
        <SectionsContainer>
          <LeftSection>
            <Row>
              <ConfigurableCheckOutInputField
                label={t('orders:details.firstName')}
                name="firstName"
                onChange={handleChange}
                required
                value={formState.firstName}
                error={validateOnSubmission(!formState.firstName)}
              />
              <ConfigurableCheckOutInputField
                label={t('orders:details.lastName')}
                name="lastName"
                onChange={handleChange}
                required
                value={formState.lastName}
                error={validateOnSubmission(!formState.lastName)}
              />
            </Row>
            <ConfigurableCheckOutInputField
              label={t('common:companyName')}
              name="companyName"
              onChange={handleChange}
              required
              value={formState.companyName}
              error={validateOnSubmission(!formState.companyName)}
            />
            <TelephoneInput
              label={t('orders:details.phoneNumber')}
              Component={CheckOutInputField}
              onChange={handleChange}
              required
              value={formState.telephone}
              error={validateOnSubmission(!formState.telephone)}
            />
            <CheckOutSubTitle>{t('orders:details.deliveryAddress')}</CheckOutSubTitle>
            <ConfigurableCheckOutInputField
              label={t('common:buildingName')}
              name="buildingName"
              onChange={handleChange}
              value={formState.buildingName}
            />
            <ConfigurableCheckOutInputField
              label={t('orders:details.streetAddress')}
              name="streetName"
              onChange={handleChange}
              required
              error={validateOnSubmission(!formState.streetName)}
              value={formState.streetName}
            />
            <Row>
              <ConfigurableCheckOutInputField
                label={t('common:city')}
                name="city"
                onChange={handleChange}
                required
                error={validateOnSubmission(!formState.city)}
                value={formState.city}
              />
              <ConfigurableCheckOutInputField
                label={t('common:postalCode')}
                name="postalCode"
                onChange={handleChange}
                required
                error={validateOnSubmission(!formState.postalCode)}
                value={formState.postalCode}
              />
            </Row>
            <MUIFormControl
              fullWidth
              variant="filled"
              size="small"
              required
              error={validateOnSubmission(!formState.country)}
              data-testid="country-select"
            >
              <CheckoutSelectLabel>{t('common:country')}</CheckoutSelectLabel>
              <CheckOutSelectField value={formState.country} name="country" onChange={handleChange}>
                {countryList.map((countryName) => (
                  <CheckOutSelectOption value={countryName} key={countryName}>
                    {countryName}
                  </CheckOutSelectOption>
                ))}
              </CheckOutSelectField>
            </MUIFormControl>
            <StyledMUICheckbox
              control={<CheckOutCheckBox name="saveDetails" onChange={handleChange} checked={formState.saveDetails} />}
              label={t('orders:details.saveDetailsForFutureOrders')}
            />
          </LeftSection>

          <OrderDetailsDivider />

          <RightSection>
            <ConfigurableCheckOutInputField
              label={useDynamoTranslation(
                'vtaas-ui:checkout:customerReferenceField',
                t('orders:details.reference', { defaultValue: '' })
              )}
              name="vtaas-ui:checkout:customerReferenceField"
              onChange={handleChange}
              value={formState['vtaas-ui:checkout:customerReferenceField']}
              required={checkoutFormConfig?.customerReference?.mandatory ?? false}
              error={validateOnSubmission(!formState['vtaas-ui:checkout:customerReferenceField'])}
            />
            <ConfigurableCheckOutInputField
              label={useDynamoTranslation(
                'vtaas-ui:checkout:customerAccountField',
                t('orders:details.customerAccount', { defaultValue: '' })
              )}
              name="vtaas-ui:checkout:customerAccountField"
              onChange={handleChange}
              value={formState['vtaas-ui:checkout:customerAccountField']}
              required={checkoutFormConfig?.customerAccount?.mandatory ?? false}
              error={validateOnSubmission(!formState['vtaas-ui:checkout:customerAccountField'])}
            />

            <LongCheckOutInputField
              label={
                <OptionalCheckoutLabel
                  label={useDynamoTranslation(
                    'vtaas-ui:checkout:messageToSellerField',
                    t('orders:details.messageToSeller', { defaultValue: '' })
                  )}
                />
              }
              name="vtaas-ui:checkout:messageToSellerField"
              onChange={handleChange}
              value={formState['vtaas-ui:checkout:messageToSellerField']}
              error={validateOnSubmission(!formState['vtaas-ui:checkout:messageToSellerField'])}
            />
          </RightSection>
        </SectionsContainer>
        <CheckoutActionBar>
          <CartButton
            startIcon={<FontAwesomeIcon icon={faEnvelope} size="sm" />}
            data-testid="order-parts-button"
            onClick={() => setValidate(true)}
            disabled={isFetching}
          >
            {t(ctaKey ?? defaultButtonText)}
          </CartButton>
        </CheckoutActionBar>
      </FormContainer>
    </CheckoutFormWrapper>
  );
}
